import { useState } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { Tag, Text } from '@kula-ai/components';
import type { SuggestedActionChipsProps } from './SuggestedActionChips.types';

// Shared suggested-action block — a "Suggested actions" title over a chip row.
// The one place every block renders follow-ups, so the header, primary-first
// sort, and per-chip click spinner stay consistent everywhere. The primary chip
// (the backend's "most obvious next click", e.g. the second half of a composite
// ask) always renders first, regardless of payload order. The clicked chip
// keeps a spinner while its turn is in flight, so the user sees WHICH action is
// running instead of the whole row silently greying out.
export const SuggestedActionChips = ({
  actions,
  onSelect,
  disabled = false,
  testIdPrefix,
  title = 'Suggested actions',
}: SuggestedActionChipsProps) => {
  const [clickedId, setClickedId] = useState<string | null>(null);
  // Once the turn finishes the row re-enables — clear the spinner chip.
  // Render-phase state adjustment (sanctioned React pattern): the guard makes
  // it settle in one extra render, and an effect would flash the stale chip.
  if (!disabled && clickedId !== null) {
    setClickedId(null);
  }
  if (actions.length === 0) return null;
  return (
    <Box>
      {title && (
        <Text variant="label" as="span" className="text-secondary-900">
          {title}
        </Text>
      )}
      <Flex wrap="wrap" gap="8px" mt={title ? '8px' : undefined}>
        {[...actions]
          .sort(
            (a, b) =>
              Number(b.kind === 'primary') - Number(a.kind === 'primary')
          )
          .map((a) => (
            <Flex key={a.id} align="center" gap="4px">
              <Tag
                label={a.label}
                size="md"
                variant={a.kind === 'primary' ? 'primary' : 'outline'}
                disabled={disabled}
                onClick={() => {
                  setClickedId(a.id);
                  onSelect(a.promptTemplate);
                }}
                className="cursor-pointer"
                data-test-id={`${testIdPrefix}-${a.id}`}
              />
              {disabled && clickedId === a.id && (
                <Spinner size="xs" color="primary.500" />
              )}
            </Flex>
          ))}
      </Flex>
    </Box>
  );
};
